// Frontend/src/components/admin/ui/AutocompleteMultiSelect.jsx
import { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiSearch, FiUser, FiX } from 'react-icons/fi';

const AutocompleteMultiSelect = ({
  label,
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Search and select...",
  required = false,
  searchKey = "label",
  displayKey = "label",
  error,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const searchValue = option[searchKey]?.toLowerCase() || '';
    return searchValue.includes(searchTerm.toLowerCase()) &&
           !selectedValues.includes(option.value);
  });

  // Get selected option objects
  const selectedOptions = options.filter(option => 
    selectedValues.includes(option.value)
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setFocusedIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleOptionSelect = (option) => {
    const newSelectedValues = [...selectedValues, option.value];
    onChange(newSelectedValues);
    setSearchTerm('');
    setFocusedIndex(-1);
    inputRef.current?.focus();
  };

  const handleOptionRemove = (valueToRemove) => {
    const newSelectedValues = selectedValues.filter(value => value !== valueToRemove);
    onChange(newSelectedValues);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIsOpen(true);
        setFocusedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
          handleOptionSelect(filteredOptions[focusedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
      case 'Backspace':
        if (searchTerm === '' && selectedValues.length > 0) {
          handleOptionRemove(selectedValues[selectedValues.length - 1]);
        }
        break;
      default:
        // No action for other keys
        break;
    }
  };

  return (
    <div className="space-y-1" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Main Input Container */}
        <div
          className={`
            min-h-[42px] w-full px-3 py-2 border rounded-md shadow-sm cursor-text
            focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500
            ${error ? 'border-red-500' : 'border-gray-300'}
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}
          `}
          onClick={handleInputClick}
        >
          <div className="flex flex-wrap items-center gap-1">
            {/* Selected Items */}
            {selectedOptions.map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-md"
              >
                <FiUser className="w-3 h-3" />
                {option[displayKey]}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionRemove(option.value);
                  }}
                  className="text-indigo-600 hover:text-indigo-800 ml-1"
                  disabled={disabled}
                >
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            ))}
            
            {/* Search Input */}
            <div className="flex-1 min-w-[120px]">
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={selectedOptions.length === 0 ? placeholder : "Search more..."}
                className="w-full border-none outline-none bg-transparent text-sm"
                disabled={disabled}
              />
            </div>
            
            {/* Dropdown Arrow */}
            <FiChevronDown 
              className={`w-4 h-4 text-gray-400 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {/* Dropdown Menu */}
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {searchTerm && (
              <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                <FiSearch className="w-3 h-3 inline mr-1" />
                Searching for "{searchTerm}"
              </div>
            )}
            
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {searchTerm ? 'No leaders found matching your search' : 'No more leaders available'}
              </div>
            ) : (
              <div className="py-1">
                {filteredOptions.map((option, index) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionSelect(option)}
                    className={`
                      w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2
                      ${index === focusedIndex ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900'}
                    `}
                  >
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <FiUser className="w-4 h-4 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {option[displayKey]}
                      </div>
                      {option.email && (
                        <div className="text-xs text-gray-500 truncate">
                          {option.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Helper Text */}
      <p className="text-xs text-gray-500">
        Type to search for leaders. Selected leaders will appear as tags above.
      </p>
    </div>
  );
};

export default AutocompleteMultiSelect;